import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { QuestionCircleTwoTone } from '@ant-design/icons';

import * as _ from 'lodash';
import { Row, Col, Divider, Button, Typography } from 'antd';

import { InfoWithLabel } from 'common/components';
import ProductOverviewBricCode from './ProductOverviewBricCode';
import ProductOverviewDimension from './ProductOverviewDimension';
import ProductOverviewQuestionButton from './ProductOverviewQuestionButton';
import ProductOverviewCertification from '../certifications/ProductOverviewCertification';

import { findDimensionEnumDisplay } from '../../mappers/mapToEnums';
import * as globalSelectors from '@redux/global/selectors';

import { useIntl } from 'react-intl';
import Messages from 'i18n/messages/product';

import { Images } from 'config/assets';
import CalculatedPropertiesModal from './CalculatedPropertiesModal';
import { OverlayValueItem } from 'common/components/overlay';
import { useCheckIsProductOverlay } from 'hooks/product';

import { formatMDY } from 'utils/formatDate';

const ProductOverviewMetadata = ({
  product,
  originProduct,
  showPackageDrawer,
  productItemModules,
  productSchema,
  productEnums,
  refContainer,
  isHiddenQuestionIcon,
  ...rest
}) => {
  const isOverlay = useCheckIsProductOverlay();
  const intl = useIntl();

  const [truncated, setTruncated] = useState(false);

  const targetMarketList = useSelector(
    globalSelectors.selectProductTargetMarket()
  );

  const getTargetMarketName = (product) => {
    // const productTargetMarketData = targetMarketList.find(
    //   (targetMarketItem) =>
    //     targetMarketItem.Numeric.toString() === product?.targetMarket
    // );

    // return productTargetMarketData?.Country || '';
    return product?.targetMarket;
  };

  const showOverlay = !!originProduct && isOverlay;

  const renderSupplier = () => {
    return (
      <Row>
        {product &&
          product.suppliers?.length > 0 &&
          product.suppliers.map((val, index) => (
            <Typography.Link
              key={val.id}
              href={`/company/${val?.id}`}
              style={{
                fontSize: '13px',
                fontWeight: 500,
                letterSpacing: '1px',
              }}
              target='_blank'
            >
              {val.name}
              {index < product.suppliers.length - 1 && ',\u00A0'}
            </Typography.Link>
          ))}
      </Row>
    );
  };

  const renderMetadata = () => {
    const metadata = [
      {
        label: 'UPC',
        info: product?.upc,
      },
      {
        label: 'SKU',
        info: product?.sku,
        showTooltip: true,
      },
      {
        label: 'Model',
        info: product?.model,
        showTooltip: true,
      },
      {
        label: 'Material',
        info: product?.material,
        showTooltip: true,
      },
      {
        label: 'Product Family',
        info: product?.productFamily,
        showTooltip: true,
      },
      {
        label: intl.formatMessage(Messages.brandName),
        info: product?.brandName,
      },

      // {
      //   label: 'Category',
      //   info: product?.category,
      // },
      // {
      //   label: 'Sub Category',
      //   info: product?.subcategory,
      // },
      // {
      //   label: 'Release Date',
      //   info: formatMDY(product?.releaseDate),
      // },
      // {
      //   label: intl.formatMessage(Messages.targetMarket),
      //   info: getTargetMarketName(product),
      // },
      // {
      //   label: intl.formatMessage(Messages.barcode),
      //   info: (
      //     <OverlayValueItem
      //       originValue={originProduct?.gtin || 'N/A'}
      //       showOverlay={showOverlay && product?.gtin !== originProduct?.gtin}
      //     >
      //       {product?.gtin}
      //     </OverlayValueItem>
      //   ),
      //   infoAppend: !isHiddenQuestionIcon ? (
      //     <ProductOverviewQuestionButton
      //       modalContent='Add content component here'
      //       showPackageDrawer={showPackageDrawer}
      //       mode='Imperial'
      //     />
      //   ) : null,
      // },
      // {
      //   label: intl.formatMessage(Messages.productType),
      //   info: product?.productType,
      // },
      {
        label: intl.formatMessage(Messages.bricCode),
        info: (
          <ProductOverviewBricCode
            brickCode={product?.brickCode}
            brickCodeName={product?.brickCodeName}
            // categories={product?.categories} // todo: update if changing later
          />
        ),
      },
      // {
      //   label: intl.formatMessage(Messages.size),
      //   info: (
      //     <RenderSize
      //       productDetail={product}
      //       originProduct={originProduct}
      //       isOverlay={isOverlay}
      //       productEnums={productEnums}
      //     />
      //   ),
      //   className: 'info-with-label_render-size',
      // },
      {
        label: intl.formatMessage(Messages.dimensions),
        info: (
          <ProductOverviewDimension
            originProduct={originProduct}
            product={product}
            showOverlay={showOverlay}
            showPackageDrawer={showPackageDrawer}
            productEnums={productEnums}
          />
        ),
        infoTextStyle: { width: '100%' },
      },
      // {
      //   label: intl.formatMessage(Messages.certification),
      //   info: (
      //     <ProductOverviewCertification
      //       productItemModules={productItemModules}
      //       productSchema={productSchema}
      //       productEnums={productEnums}
      //     />
      //   ),
      //   infoTextStyle: { minWidth: '100%' },
      // },
    ];

    // if (product?.isPrivateLabel) {
    //   metadata.splice(2, 0, {
    //     label: intl.formatMessage(Messages.supplierNames),
    //     info: renderSupplier(),
    //   });
    // }

    return metadata.map((item, index) => {
      const props = {
        ...item,
        labelAlign: 'right',
        labelColProps: {
          ...item?.labelColProps,
          flex: '115px',
        },
        infoColProps: {
          flex: 1,
        },
        gutter: [20, 0],
      };

      return <InfoWithLabel key={index} {...props} />;
    });
  };

  const primaryImg = () => {
    return product?.productPrimaryImage || Images.RIVIR_LOGO_DEFAULT;
  };

  return (
    <Row style={{ flexWrap: 'nowrap', height: '100%' }} ref={refContainer}>
      <Col flex={'250px'} className='product-overview__primary-image-col'>
        <div
          className='product-overview__primary-image'
          style={{ backgroundImage: `url(${primaryImg()})` }}
        />
      </Col>
      <Col flex={0}>
        <Divider type='vertical' style={{ height: '100%' }} />
      </Col>
      <Col
        className='product-overview__metadata'
        style={{ overflow: 'hidden' }}
        flex={1}
      >
        {renderMetadata()}
      </Col>
    </Row>
  );
};

const RenderSize = ({
  productDetail,
  originProduct,
  isOverlay,
  productEnums,
}) => {
  const [visible, setVisible] = useState();

  const uiEnumDisplay =
    findDimensionEnumDisplay(productDetail?.primarySizeUOM, productEnums) ?? '';

  const getPrimarySize = (productDetail) => {
    return isNaN(parseFloat(productDetail?.primarySize))
      ? 'N/A'
      : Math.round(parseFloat(productDetail?.primarySize) * 100) / 100 +
          ' ' +
          uiEnumDisplay;
  };

  const showOverlay = !!originProduct && isOverlay;

  return (
    <>
      <OverlayValueItem
        originValue={
          originProduct?.netContentsStatement || getPrimarySize(originProduct)
        }
        checkShowOverlayFn={() => {
          const overlayValue =
            productDetail?.netContentsStatement ||
            getPrimarySize(productDetail);

          const originValue =
            originProduct?.netContentsStatement ||
            getPrimarySize(originProduct);

          return showOverlay && overlayValue !== originValue;
        }}
      >
        {productDetail?.netContentsStatement
          ? productDetail?.netContentsStatement
          : getPrimarySize(productDetail)}
      </OverlayValueItem>
      <Button type='link' onClick={() => setVisible(true)}>
        <QuestionCircleTwoTone style={{ fontSize: 16 }} />
      </Button>
      <CalculatedPropertiesModal
        visible={visible}
        setVisible={setVisible}
        primarySize={getPrimarySize(productDetail)}
        productDetail={productDetail}
      />
    </>
  );
};

ProductOverviewMetadata.propTypes = {
  product: PropTypes.object,
  showPackageDrawer: PropTypes.func,
};

export default ProductOverviewMetadata;
